import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface GalleryImage {
  id: string;
  partner_id: string;
  image_url: string;
  title: string;
  description: string;
  created_at: string;
  uploaded_by: string;
}

export default function AdminGallery() {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    partnerId: '',
    title: '',
    description: '',
    file: null as File | null
  });

  const partners = [
    { id: '1', name: '박성숙 - 남도음식전문가' },
    { id: '2', name: '복영문 - 제이앤비농장 (유자농장)' },
    { id: '3', name: '정종권 - 봄햇살케어팜 (숲체험)' },
    { id: '4', name: '최현귀 - 두울리 (라벤더농장)' },
    { id: '5', name: '박지영 - 세모실커피농장' },
    { id: '6', name: '김은정 - 한옥펜션' },
    { id: '7', name: '손현숙 - 하루애꽃 (치유체험농장)' }
  ];

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    try {
      const { data, error } = await supabase
        .from('partner_images')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setImages(data || []);
    } catch (error) {
      console.error('이미지 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!uploadForm.file || !uploadForm.partnerId) {
      alert('업체와 이미지 파일을 선택해주세요.');
      return;
    }

    setUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('로그인이 필요합니다.');

      // 파일 업로드
      const fileExt = uploadForm.file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `partner-images/${uploadForm.partnerId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, uploadForm.file);

      if (uploadError) throw uploadError;

      // 공개 URL 가져오기
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      // DB에 저장
      const { error: dbError } = await supabase
        .from('partner_images')
        .insert({
          partner_id: uploadForm.partnerId,
          image_url: publicUrl,
          title: uploadForm.title || uploadForm.file.name,
          description: uploadForm.description || '',
          uploaded_by: user.id
        });

      if (dbError) throw dbError;

      alert('이미지가 성공적으로 업로드되었습니다!');
      setUploadForm({ partnerId: '', title: '', description: '', file: null });
      loadImages();
    } catch (error: any) {
      console.error('업로드 오류:', error);
      alert(`업로드 실패: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (image: GalleryImage) => {
    if (!confirm('이 이미지를 삭제하시겠습니까?')) return;

    try {
      // Storage에서 파일 삭제
      const filePath = image.image_url.split('/').slice(-3).join('/');
      await supabase.storage.from('images').remove([filePath]);

      // DB에서 삭제
      const { error } = await supabase
        .from('partner_images')
        .delete()
        .eq('id', image.id);

      if (error) throw error;

      alert('이미지가 삭제되었습니다.');
      loadImages();
      setSelectedImage(null);
    } catch (error: any) {
      console.error('삭제 오류:', error);
      alert(`삭제 실패: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <i className="ri-loader-4-line text-3xl text-emerald-600 animate-spin"></i>
      </div>
    );
  }

  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">갤러리 관리</h2>

      {/* 이미지 업로드 폼 */}
      <div className="bg-gray-50 rounded-lg p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">새 이미지 업로드</h3>
        <form onSubmit={handleFileUpload} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              업체 선택 <span className="text-red-500">*</span>
            </label>
            <select
              value={uploadForm.partnerId}
              onChange={(e) => setUploadForm({ ...uploadForm, partnerId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              required
            >
              <option value="">업체를 선택하세요</option>
              {partners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              이미지 제목
            </label>
            <input
              type="text"
              value={uploadForm.title}
              onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="이미지 제목 (선택사항)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              설명
            </label>
            <textarea
              value={uploadForm.description}
              onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
              placeholder="이미지 설명 (선택사항)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              이미지 파일 <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 file:cursor-pointer"
              required
            />
          </div>

          <button
            type="submit"
            disabled={uploading}
            className="w-full bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {uploading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                업로드 중...
              </span>
            ) : (
              '이미지 업로드'
            )}
          </button>
        </form>
      </div>

      {/* 이미지 목록 */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {images.map((image) => (
          <div
            key={image.id}
            className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => setSelectedImage(image)}
          >
            <div className="w-full h-48">
              <img
                src={image.image_url}
                alt={image.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-4">
              <h4 className="font-semibold text-gray-900 mb-1 truncate">{image.title}</h4>
              <p className="text-sm text-gray-600 truncate">
                {partners.find(p => p.id === image.partner_id)?.name || '알 수 없음'}
              </p>
            </div>
          </div>
        ))}
      </div>

      {images.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <i className="ri-image-line text-5xl mb-4 w-12 h-12 flex items-center justify-center mx-auto"></i>
          <p>등록된 이미지가 없습니다.</p>
        </div>
      )}

      {/* 이미지 상세 모달 */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="bg-white rounded-lg max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <img
                src={selectedImage.image_url}
                alt={selectedImage.title}
                className="w-full h-auto"
              />
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2">{selectedImage.title}</h3>
              <p className="text-gray-600 mb-4">{selectedImage.description}</p>
              <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                <span>{partners.find(p => p.id === selectedImage.partner_id)?.name}</span>
                <span>{new Date(selectedImage.created_at).toLocaleDateString()}</span>
              </div>
              <button
                onClick={() => handleDelete(selectedImage)}
                className="w-full bg-red-600 text-white py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-delete-bin-line mr-2"></i>
                이미지 삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}